/**
 * Elektrische Leistungsberechnung für Wärmepumpen-Kompressoren
 * 
 * Formeln:
 * 1-Phase (230V): P[W] = U × I × cosφ × η
 * 3-Phase (400V): P[W] = √3 × U × I × cosφ × η
 */

// Standardwerte
const DEFAULTS = {
  voltage_1phase: 230,
  voltage_3phase: 400,
  cosphi: 0.85,
  eta: 0.90,
  sqrt3: Math.sqrt(3)
};

/**
 * Berechnet elektrische Leistung für 1-Phase
 * @param {number} U - Spannung in Volt (Standard: 230V)
 * @param {number} I - Strom in Ampere
 * @param {number} cosphi - Leistungsfaktor (Standard: 0.85)
 * @param {number} eta - Wirkungsgrad (Standard: 0.90)
 * @returns {{watts: number, kilowatts: number}}
 */
export function calcPowerSinglePhase(U = DEFAULTS.voltage_1phase, I, cosphi = DEFAULTS.cosphi, eta = DEFAULTS.eta) {
  if (!I || I <= 0) return { watts: 0, kilowatts: 0 };
  
  const watts = U * I * cosphi * eta;
  return {
    watts: Math.round(watts),
    kilowatts: parseFloat((watts / 1000).toFixed(3))
  };
}

/**
 * Berechnet elektrische Leistung für 3-Phase
 * @param {number} U - Spannung in Volt (Standard: 400V)
 * @param {number} I - Strom in Ampere
 * @param {number} cosphi - Leistungsfaktor (Standard: 0.85)
 * @param {number} eta - Wirkungsgrad (Standard: 0.90)
 * @returns {{watts: number, kilowatts: number}}
 */
export function calcPowerThreePhase(U = DEFAULTS.voltage_3phase, I, cosphi = DEFAULTS.cosphi, eta = DEFAULTS.eta) {
  if (!I || I <= 0) return { watts: 0, kilowatts: 0 };
  
  const watts = DEFAULTS.sqrt3 * U * I * cosphi * eta;
  return {
    watts: Math.round(watts),
    kilowatts: parseFloat((watts / 1000).toFixed(3))
  };
}

/**
 * Berechnet COP (Coefficient of Performance)
 * COP = Heizleistung / Elektrische Leistung
 * @param {number} heatingPowerKW - Heizleistung in kW
 * @param {number} electricalPowerKW - Elektrische Leistung in kW
 * @returns {{cop: number, efficiency_percent: number}}
 */
export function calculateCOP(heatingPowerKW, electricalPowerKW) {
  if (!heatingPowerKW || !electricalPowerKW || electricalPowerKW <= 0) {
    return { cop: 0, efficiency_percent: 0 };
  }
  
  const cop = heatingPowerKW / electricalPowerKW;
  return {
    cop: parseFloat(cop.toFixed(2)),
    efficiency_percent: Math.round(cop * 100)
  };
}

/**
 * Berechnet aus gemessenen Spannungen die durchschnittliche Spannung
 * und ermittelt ob es sich um 1-Phase oder 3-Phase handelt
 * @param {number} L1 - Spannung L1-N
 * @param {number} L2 - Spannung L2-N (optional)
 * @param {number} L3 - Spannung L3-N (optional)
 * @returns {{avgVoltage: number, is3Phase: boolean, phaseCount: number}}
 */
export function analyzeVoltage(L1, L2, L3) {
  const phases = [L1, L2, L3].filter(v => v && v > 0);
  const phaseCount = phases.length;
  
  if (phaseCount === 0) {
    return { avgVoltage: 0, is3Phase: false, phaseCount: 0 };
  }
  
  const avgVoltage = phases.reduce((sum, v) => sum + parseFloat(v), 0) / phaseCount;
  
  return {
    avgVoltage: Math.round(avgVoltage),
    is3Phase: phaseCount === 3,
    phaseCount
  };
}
